'use client';

import { normalizeProductQuantity } from '@eliadmin/core/src/utils/product-utils';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import type { CartItemPackages } from '@eliadmin/core/src/api/cart/cart-types';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { trackEcommerce } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { AddToCartDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { AddToCartVariantDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import type { ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useAddToCart } from '@eliadmin/core/src/hooks/cart/use-add-to-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SelectedServices } from '@eliadmin/core/src/stores/variant-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiShoppingCart } from 'react-icons/fi';

type AddToCartButtonProps = ButtonProps & {
  product: ProductDetailData | Product;
  quantity?: number;
  className?: {
    buttonLabel?: string;
  };
  showIcon?: boolean;
  sellable?: boolean;
  customText?: string;
  variant?: 'dark' | 'default';
  type?: string;
  customButton?: boolean;
};
export function AddToCartButton({
  className,
  showIcon,
  sellable,
  product,
  customText,
  variant,
  quantity = 1,
  customButton = false,
  ...props
}: AddToCartButtonProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();
  const openDialog = useDialogStore(s => s.open);

  const addToCartMutation = useAddToCart();

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedPackages = useVariantsStore(state => state.selectedPackages);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string>('EUR');

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES, locale],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies?.data.find(currency => currency.symbol === currencySymbol)?.id || currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const productQuery = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'detail', locale, product.id, me?.customer.id ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: product.slug, locale, authToken: me?.authToken })
        .then(res => res['App\\Models\\Product'])
        .catch(() => null),
    enabled: false,
  });

  const sortedSuppliers = [...(product.suppliers ?? [])].sort((a, b) => a.position - b.position);

  const firstSupplier = sortedSuppliers[0];
  const isProductEnded = product.status === 'ended';
  const hasUnresolvedVariants =
    (!!product.has_variants || !!product.variants?.length) && !selectedVariant;
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product);
  const priceOnRequest = !hasUnresolvedVariants && priceDisplay.priceOnRequest;

  const handleAddToCart = async () => {
    if (!isInitialized || priceOnRequest || (!sellable && quantity < 1)) return;

    const fullProduct = product.has_variants
      ? await productQuery.refetch().then(res => res.data)
      : product;

    if (fullProduct?.variants?.length) {
      openDialog({
        id: AddToCartVariantDialog.id,
        content: (
          <VariantsProvider
            state={{
              product: fullProduct,
              selectedPackages,
              selectedVariant,
              selectedQuantity,
              selectedServices,
              selectedValues,
            }}
          >
            <AddToCartVariantDialog
              product={fullProduct}
              quantity={normalizeProductQuantity(quantity, fullProduct)}
            />
          </VariantsProvider>
        ),
      });
    } else {
      await addToCartMutation.mutateAsync({
        locale,
        // TODO: implement quantity
        body: {
          item: {
            product: { id: product.id },
            sub_items: {
              services: selectedServices
                ? selectedServices.map((selectedService: SelectedServices) => {
                    return {
                      id: selectedService.id,
                    };
                  })
                : [],
              packages: selectedPackages
                ? selectedPackages.map(selectedPackage => {
                    return {
                      id: selectedPackage.id,
                    } as CartItemPackages;
                  })
                : [],
            },
          },
          qty: normalizeProductQuantity(quantity, product),
        },
      });
      const productPrice = priceDisplay.currentWithVat;
      if (productPrice != null) {
        trackEcommerce.addToCart({
          id: product.id.toString(),
          name: product.name,
          price: productPrice,
          currency: currencyISO,
        });
      }
      openDialog({
        id: AddToCartDialog.id,
        content: <AddToCartDialog product={product} title="text631" />,
      });
    }
  };

  if (!firstSupplier?.is_affiliate && priceOnRequest) return null;

  return (
    <>
      {customButton ? (
        <Button
          className={cn('w-fit', className)}
          aria-label={`${t('text172')}: ${product.name}`}
          onClick={e => {
            e.stopPropagation();
            e.nativeEvent.preventDefault();
            handleAddToCart();
          }}
          disabled={!isInitialized || !sellable || isProductEnded}
          variant={'default'}
          isLoading={addToCartMutation.isPending || productQuery.isFetching}
          {...props}
        >
          <span
            className={cn(
              'line-clamp-1 hidden text-ellipsis @[200px]:inline',
              className?.buttonLabel,
            )}
          >
            {t('text172')}
          </span>
        </Button>
      ) : (
        <>
          {!firstSupplier?.is_affiliate && (
            <Button
              className={cn('w-full gap-2', className)}
              aria-label={`${customText ? t(customText) : t('text227')}: ${product.name}`}
              onClick={e => {
                e.stopPropagation();
                e.nativeEvent.preventDefault();
                handleAddToCart();
              }}
              disabled={!isInitialized || !sellable || isProductEnded}
              variant={variant ?? 'dark'}
              isLoading={addToCartMutation.isPending || productQuery.isFetching}
              {...props}
            >
              {showIcon && <FiShoppingCart className="h-5 w-5" />}
              <span
                className={cn(
                  'line-clamp-1 hidden text-ellipsis whitespace-nowrap @[200px]:inline',
                  className?.buttonLabel,
                )}
              >
                {customText ? t(customText) : t('text227')}
              </span>
            </Button>
          )}
          {firstSupplier?.is_affiliate && (
            <Button
              className={cn('w-full gap-2', className)}
              asChild
              variant={variant ?? 'dark'}
              isLoading={addToCartMutation.isPending || productQuery.isFetching}
              {...props}
            >
              <a href={firstSupplier.url ?? '#'}>
                {showIcon && <FiShoppingCart className="h-5 w-5" />}
                <span
                  className={cn(
                    'line-clamp-1 inline text-ellipsis text-center',
                    className?.buttonLabel,
                  )}
                >
                  {customText ? t(customText) : t('text1226')}
                </span>
              </a>
            </Button>
          )}
        </>
      )}
    </>
  );
}

// /**
//  * Deprecated and empty not to break other eshops
//  * @deprecated
//  */
// function addProductToAnalytics(product: ProductDetailData | Product, quantity: number = 1) {
//   //
// }
